import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
export const Route = createFileRoute('/_layout/blog/telegram-notifications/')({
    component: TelegramNotificationsPost,
    head: () => ({
        meta: [
            { title: 'Real-time Telegram notifications: what Adora sends, where, and why — Adora' },
            {
                name: 'description',
                content: 'Feedback submissions, payout reminders, photo nudges. The complete map of Adora’s outbound notifications and how to configure them per workspace and per campaign.',
            },
            { property: 'og:title', content: 'Real-time Telegram notifications — Adora' },
            {
                property: 'og:description',
                content: 'Feedback submissions, payout reminders, photo nudges. The complete map of Adora’s outbound notifications and how to configure them per workspace and per campaign.',
            },
        ],
    }),
});
function TelegramNotificationsPost() {
    return (<article className="mx-auto max-w-3xl px-6 md:px-8 py-12">
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3.5 w-3.5"/>
                All posts
            </Link>

            <header className="mt-6 space-y-3">
                <p className="text-xs uppercase tracking-wide text-primary">Reference · 2026-04-28</p>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                    Real-time Telegram notifications: what Adora sends, where, and why
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                    The full map of outbound Telegram messages from Adora — three streams, two configuration levels, one
                    bot. How to tune what fires, where it goes, and how to test before you ship.
                </p>
            </header>

            <div className="prose prose-neutral dark:prose-invert mt-10 max-w-none">
                <h2>The three notification streams</h2>

                <h3>1. Feedback submissions</h3>
                <p>
                    When a visitor submits a feedback form on a feedback-mode campaign, Adora pushes the formatted
                    message (rating, comment, optional name + email, source Smart Link) to your configured chat
                    immediately. This is the primary stream for every workspace — it's why most teams set up the bot in
                    the first place.
                </p>
                <p>
                    <strong>Audience:</strong> the campaign owner / customer-facing team.
                    <br />
                    <strong>Configured at:</strong> workspace default + per-campaign override.
                    <br />
                    <strong>Trigger:</strong> public form submit (synchronous today; queued in a follow-up).
                </p>

                <h3>2. Driver payout reminders (internal)</h3>
                <p>
                    Adora's internal driver-management module advances each payout through the
                    <code> pending → due → overdue </code> states on a daily cron. When a payout becomes
                    <code> due </code> or stays <code> overdue </code>, the bot pings the staff chat with a structured
                    reminder (driver name, amount, period, reference link).
                </p>
                <p>
                    <strong>Audience:</strong> internal staff handling payouts.
                    <br />
                    <strong>Configured at:</strong> internal settings → <code>driver.notifier.payout_chat_id</code>.
                    <br />
                    <strong>Cadence:</strong> max 5 reminders per payout (configurable), enforced by
                    <code> last_reminded_at </code> on each row so you don't spam the chat.
                </p>

                <h3>3. Driver photo nudges (internal)</h3>
                <p>
                    Each driver assignment has a <em>photo cycle</em> (weekly / bi-weekly / monthly). When a cycle's
                    window passes without a proof-of-display photo logged, the bot nudges the staff chat.
                </p>
                <p>
                    <strong>Audience:</strong> internal staff logging photos.
                    <br />
                    <strong>Configured at:</strong> internal settings → <code>driver.notifier.photo_chat_id</code>.
                    <br />
                    <strong>Cadence:</strong> max 3 nudges per (assignment, period), tracked via
                    <code> last_photo_reminded_at</code>.
                </p>

                <h2>Two levels of configuration</h2>
                <p>For feedback (the most common case), there are two places the chat ID can live:</p>
                <ul>
                    <li>
                        <strong>Workspace default</strong> — <code>workspace.telegramChatId</code>. Set in
                        <code> /console/workspace</code>. Applies to every feedback campaign in the workspace unless
                        explicitly overridden.
                    </li>
                    <li>
                        <strong>Per-campaign override</strong> — <code>campaign.feedbackTelegramChatId</code>. Set on
                        the feedback-mode campaign's edit panel. Wins over the workspace default for that one campaign.
                    </li>
                </ul>
                <p>
                    Resolution order at delivery time: <code>campaign.feedbackTelegramChatId</code> →{' '}
                    <code>workspace.telegramChatId</code> → skip with a warning log. The admin list shows
                    <code> delivered = false </code> for any submission that couldn't find a destination, so you can
                    spot misconfiguration quickly.
                </p>

                <h2>Testing before you ship</h2>
                <p>Every chat-ID input in Adora has two affordances next to it:</p>
                <ul>
                    <li>
                        A <strong>?</strong> help icon → opens a 3-step modal (add bot → <code>/chatid</code> → paste).
                        The modal also fetches the bot's <code>@username</code> from Telegram so you see the exact
                        handle to add.
                    </li>
                    <li>
                        A <strong>Test</strong> button → posts a one-shot test message to the chat ID currently in the
                        input. Toast tells you whether Telegram accepted it.
                    </li>
                </ul>

                <h2>What you don't get (yet)</h2>
                <ul>
                    <li>
                        <strong>Per-user channel routing.</strong> Notifications go to the <em>chat</em>, not to
                        specific people inside it. Use Telegram's own mention features for that.
                    </li>
                    <li>
                        <strong>Reply-back to a feedback submission via Telegram.</strong> Inbound webhook only handles
                        photo / payout staff workflows today. Feedback replies are roadmap.
                    </li>
                    <li>
                        <strong>Throttling.</strong> No artificial rate-limit between submissions on the Adora side.
                        Telegram's own per-chat limit (~30 msg/sec) is the hard ceiling.
                    </li>
                </ul>

                <h2>If something doesn't fire</h2>
                <ol>
                    <li>Check the bot is a member of the chat (it can't post otherwise).</li>
                    <li>
                        Check the bot token is configured in <code>/console/internal/settings → Telegram routing</code>{' '}
                        (or the
                        <code> TELEGRAM_BOT_TOKEN </code> env var).
                    </li>
                    <li>
                        For feedback specifically, verify the workspace OR campaign has a chat ID set. Submissions with
                        no destination are persisted but flagged <code>delivered = false</code>.
                    </li>
                    <li>Hit the Test button. The toast will tell you exactly what Telegram returned.</li>
                </ol>
            </div>

            <footer className="mt-12 border-t pt-6 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5"/>
                    All posts
                </Link>
                <Link to="/blog/telegram-bot-setup" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                    Read the bot setup guide
                    <ArrowRight className="h-4 w-4"/>
                </Link>
            </footer>
        </article>);
}
